import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { RocFormData } from '../../../../types/RocFormData'
import { RocFormField } from '../../../../types/RocFormField'
import { RocAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Vitals subsection adapter
 * Handles all vital signs measurements (8 fields)
 */
export class VitalsAdapter extends BaseSubsectionAdapter<RocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Direct field extractions for all vital signs
    const temperature = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_TEMPERATURE
    )?.at(0)

    const pulse = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_PULSE
    )?.at(0)

    const respirations = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_RESPIRATIONS
    )?.at(0)

    const o2Saturation = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_O2_SATURATION
    )?.at(0)

    const bloodPressure = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_BLOOD_PRESSURE
    )?.at(0)

    const height = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_HEIGHT
    )?.at(0)

    const bloodSugar = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_BLOOD_SUGAR
    )?.at(0)

    const weight = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_WEIGHT
    )?.at(0)

    return {
      [RocFormField.VITALS_TEMPERATURE]: temperature,
      [RocFormField.VITALS_PULSE]: pulse,
      [RocFormField.VITALS_RESPIRATIONS]: respirations,
      [RocFormField.VITALS_O2_SATURATION]: o2Saturation,
      [RocFormField.VITALS_BLOOD_PRESSURE]: bloodPressure,
      [RocFormField.VITALS_HEIGHT]: height,
      [RocFormField.VITALS_BLOOD_SUGAR]: bloodSugar,
      [RocFormField.VITALS_WEIGHT]: weight
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RocFormField.VITALS_TEMPERATURE)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_TEMPERATURE,
        value: this.getFormDataField(RocFormField.VITALS_TEMPERATURE)
      })
    }

    if (this.formDataFieldExists(RocFormField.VITALS_PULSE)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_PULSE,
        value: this.getFormDataField(RocFormField.VITALS_PULSE)
      })
    }

    if (this.formDataFieldExists(RocFormField.VITALS_RESPIRATIONS)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_RESPIRATIONS,
        value: this.getFormDataField(RocFormField.VITALS_RESPIRATIONS)
      })
    }

    if (this.formDataFieldExists(RocFormField.VITALS_O2_SATURATION)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_O2_SATURATION,
        value: this.getFormDataField(RocFormField.VITALS_O2_SATURATION)
      })
    }

    if (this.formDataFieldExists(RocFormField.VITALS_BLOOD_PRESSURE)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_BLOOD_PRESSURE,
        value: this.getFormDataField(RocFormField.VITALS_BLOOD_PRESSURE)
      })
    }

    if (this.formDataFieldExists(RocFormField.VITALS_HEIGHT)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_HEIGHT,
        value: this.getFormDataField(RocFormField.VITALS_HEIGHT)
      })
    }

    if (this.formDataFieldExists(RocFormField.VITALS_BLOOD_SUGAR)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_BLOOD_SUGAR,
        value: this.getFormDataField(RocFormField.VITALS_BLOOD_SUGAR)
      })
    }

    if (this.formDataFieldExists(RocFormField.VITALS_WEIGHT)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_VITALS_WEIGHT,
        value: this.getFormDataField(RocFormField.VITALS_WEIGHT)
      })
    }

    return mutations
  }
}
